import React, { useCallback, useEffect, useRef, useState } from "react";
import { axiosRequest } from "../../utility/axios";

const POLL_MS = 8000;
const PAGE_CYCLE_MS = 9000;

const COLORS = [
  "#FFD700", "#FFA500", "#FFEC8B", "#FF6B6B", "#4ECDC4", "#00FF7F", "#00CED1", "#FF69B4",
];
// A wider, more saturated spread for the once-a-day winner announcement — the everyday
// "new leader" pop keeps the smaller palette above so it doesn't feel like a big event.
const DAY_WINNER_COLORS = [
  "#FFD700", "#FFA500", "#FF4D4D", "#FF69B4", "#DA70D6", "#8A2BE2", "#4169E1", "#00BFFF",
  "#00E5A0", "#39FF14", "#FFEC8B", "#FFFFFF", "#FF1493", "#FFC300",
];

const PRIZE_TABLE = [
  { from: 1, to: 1, prize: 250000, players: 1 },
  { from: 2, to: 2, prize: 120000, players: 1 },
  { from: 3, to: 3, prize: 80000, players: 1 },
  { from: 4, to: 5, prize: 50000, players: 2 },
  { from: 6, to: 10, prize: 30000, players: 5 },
  { from: 11, to: 15, prize: 10000, players: 5 },
  { from: 16, to: 17, prize: 7500, players: 2 },
  { from: 18, to: 20, prize: 5000, players: 3 },
  { from: 21, to: 30, prize: 2000, players: 10 },
];
const TOTAL_PRIZE_POOL = PRIZE_TABLE.reduce((s, t) => s + t.prize * t.players, 0);
const IPHONE_LIST_SIZE = 20; // how many standings to show for the daily/weekend boards (only rank 1 wins the prize)

// Milestone leaderboard (Aug 8-20): unlike the rank-based contest, a player's prize is
// whichever is the highest tier *they* have crossed (via points OR hours), not their
// position vs other players -- any number of players can independently reach any tier.
// Kept in sync with dao/milestoneLeaderboard.js MILESTONE_TIERS (display only; points/tiers
// are computed server-side and returned per player, this local copy is just for the tier
// prize lookup / teaser cycling).
const MILESTONE_TIERS = [
  { target: 8000, hours: 104, prize: 10000 },
  { target: 10000, hours: 117, prize: 25000 },
  { target: 11000, hours: 130, prize: 50000 },
  { target: 14000, hours: 182, prize: 100000 },
  { target: 16000, hours: 195, prize: 125000 },
  { target: 18000, hours: 208, prize: 250000 },
  { target: 20000, hours: 234, prize: 500000 },
];
const MILESTONE_POOL_LABEL = "₹20,00,000+"; // open-ended -- uncapped winners, not a fixed split
const MILESTONE_START = new Date("2026-08-08T00:00:00+05:30"); // kept in sync with dao/milestoneLeaderboard.js

const VARIANTS = {
  contest: { title: "Hourly Played Leaderboard", rankingsUrl: "/leaderboard/rankings/public" },
  daily: { title: "Daily iPhone Leaderboard", rankingsUrl: "/leaderboard/rankings/daily/public" },
  weekend: { title: "Weekend iPhone Leaderboard", rankingsUrl: "/leaderboard/rankings/weekend/public" },
  milestone: { title: "Milestone Leaderboard", rankingsUrl: "/leaderboard/rankings/milestone/public" },
};

// The Weekend board is the same iPhone-prize board as Daily, just accumulated across
// Fri-Sun instead of a single day (see controller/leaderboard.js's getISTWeekendBounds) —
// they share the same light Apple-style theme, prize, and layout throughout this file.
const isIphoneVariant = (variant) => variant === "daily" || variant === "weekend";

// A cooler, Apple-toned spread for the iPhone boards' everyday "new leader" pop, so it
// matches the light iPhone theme instead of the hourly board's gold/orange palette.
const DAILY_LEADER_COLORS = ["#0071e3", "#5AC8FA", "#34C759", "#FF375F", "#BF5AF2", "#FF9F0A", "#64D2FF", "#FFFFFF"];

// tiedWith is the size of the rank's tie group (1 = no tie) — a cash prize splits evenly
// across everyone sharing that rank; the iPhone boards have no cash value to divide, so
// ties there are only flagged, not split.
const prizeForRank = (rank, variant, tiedWith = 1) => {
  if (isIphoneVariant(variant)) return rank === 1 ? "iPhone 17" : null;
  const t = PRIZE_TABLE.find((t) => rank >= t.from && rank <= t.to);
  if (!t) return null;
  return tiedWith > 1 ? Math.round(t.prize / tiedWith) : t.prize;
};

const fmtINR = (n) => Number(n).toLocaleString("en-IN");
const fmtPrize = (p) => (typeof p === "number" ? fmtINR(p) : p);
const fmtPts = (p, decimals = 1) => {
  const mult = 10 ** decimals;
  const n = Math.round((p || 0) * mult) / mult;
  return n.toLocaleString("en-IN", { minimumFractionDigits: n % 1 === 0 ? 0 : decimals, maximumFractionDigits: decimals });
};
// Two players with genuinely different points can still round to the same 1-decimal
// display value (e.g. 614.32 vs 614.29 both show "614.3"), which reads as a tie even
// though their real ranks differ. Bump to 2 decimals only for the entries actually
// involved in such a collision, so a true tie (same rank) still displays identically.
const computeDisplayDecimals = (rankings) => {
  const decimalsById = {};
  rankings.forEach((cur, i) => {
    let dec = 1;
    for (const n of [rankings[i - 1], rankings[i + 1]]) {
      if (n && n.rank !== cur.rank && Math.round(n.total_points * 10) === Math.round(cur.total_points * 10)) {
        dec = 2;
        break;
      }
    }
    decimalsById[cur._id] = dec;
  });
  return decimalsById;
};
// How many players share a given rank number — players with equal points carry the same
// backend-computed rank, so a cash prize for that rank splits across all of them.
const tieGroupSizeFor = (rankings, rank) => rankings.filter((x) => x.rank === rank).length;
const fmtHrs = (mins) => `${(Math.max(0, mins || 0) / 60).toFixed(1)}h`;
const fmtHoursNeeded = (h) => (h == null ? "" : h < 1 ? `${Math.round(h * 60)}m` : `${h.toFixed(1)}h`);
const IST_TZ = "Asia/Kolkata";
const fmtDateShort = (d) => new Date(d).toLocaleDateString("en-IN", { timeZone: IST_TZ, day: "2-digit", month: "short" });
// IST "YYYY-MM-DD" for any instant (todayIST below is the "now" case).
const istDateOf = (d) => {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: IST_TZ, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(d));
  const get = (t) => parts.find((p) => p.type === t)?.value;
  return `${get("year")}-${get("month")}-${get("day")}`;
};
const todayIST = () => istDateOf(Date.now());
// 0=Sun,1=Mon,...,6=Sat for an IST "YYYY-MM-DD" string — used to catch the Sunday->Monday
// rollover specifically (the weekend board's winner-decided moment), not every midnight.
const istDayOfWeek = (dateStr) => {
  const [y, mo, d] = dateStr.split("-").map(Number);
  return new Date(Date.UTC(y, mo - 1, d)).getUTCDay();
};

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@700;900&family=Cinzel:wght@500;700;900&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
.ld-root{position:fixed;inset:0;background:radial-gradient(ellipse at 30% 0%,#ffffff 0%,#faf6ec 55%,#f2ead4 100%);
  overflow:hidden;font-family:'Cinzel','Georgia',serif;color:#2b2312;display:flex;flex-direction:column;}
.ld-canvas{position:fixed;inset:0;pointer-events:none;z-index:5;}
.ld-orb{position:absolute;border-radius:50%;filter:blur(110px);pointer-events:none;}
.ld-orb-1{width:900px;height:900px;background:radial-gradient(circle,rgba(218,165,32,.16) 0%,transparent 65%);top:-350px;left:-300px;animation:orb-drift 16s ease-in-out infinite;}
.ld-orb-2{width:700px;height:700px;background:radial-gradient(circle,rgba(255,165,0,.13) 0%,transparent 65%);bottom:-250px;right:-250px;animation:orb-drift 13s ease-in-out infinite reverse;}
@keyframes orb-drift{0%,100%{transform:translate(0,0) scale(1);}50%{transform:translate(40px,-30px) scale(1.07);}}
.ld-corner{position:fixed;width:64px;height:64px;border-color:rgba(184,134,11,.5);border-style:solid;pointer-events:none;z-index:6;animation:corner-pulse 3s ease-in-out infinite;}
.ld-corner.tl{top:16px;left:16px;border-width:2px 0 0 2px;}
.ld-corner.tr{top:16px;right:16px;border-width:2px 2px 0 0;}
.ld-corner.bl{bottom:16px;left:16px;border-width:0 0 2px 2px;}
.ld-corner.br{bottom:16px;right:16px;border-width:0 2px 2px 0;}
@keyframes corner-pulse{0%,100%{border-color:rgba(184,134,11,.35);}50%{border-color:rgba(184,134,11,.85);}}
.ld-qr-panel{position:fixed;right:26px;top:26px;z-index:15;width:clamp(140px,16vw,240px);
  background:rgba(255,253,247,.92);border:1px solid rgba(184,134,11,.5);border-radius:16px;padding:8px;
  box-shadow:0 0 16px rgba(184,134,11,.18),0 8px 24px rgba(0,0,0,.12);animation:qr-glow 3s ease-in-out infinite;}
.ld-qr-panel img{width:100%;display:block;border-radius:10px;}
@keyframes qr-glow{0%,100%{box-shadow:0 0 14px rgba(184,134,11,.15),0 8px 24px rgba(0,0,0,.12);}
  50%{box-shadow:0 0 28px rgba(184,134,11,.4),0 8px 24px rgba(0,0,0,.12);}}
.ld-header{position:relative;z-index:10;text-align:center;padding:clamp(10px,1.6vh,22px) 16px clamp(4px,1vh,10px);flex:0 0 auto;}
.ld-club{font-size:clamp(11px,1.4vw,18px);letter-spacing:6px;color:#8a6d00;font-weight:600;text-transform:uppercase;animation:fade-glow 3.5s ease-in-out infinite;}
.ld-title{font-family:'Cinzel Decorative','Georgia',serif;font-weight:900;font-size:clamp(22px,3.6vw,52px);
  background:linear-gradient(90deg,#7a5900,#B8860B,#DAA520,#FFA500,#B8860B,#7a5900);background-size:300%;
  -webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;
  animation:shimmer 4s linear infinite,title-glow 2.5s ease-in-out infinite;letter-spacing:1px;margin-top:2px;}
@keyframes shimmer{0%{background-position:0% center;}100%{background-position:300% center;}}
@keyframes title-glow{0%,100%{filter:drop-shadow(0 2px 4px rgba(120,90,10,.25));}50%{filter:drop-shadow(0 2px 10px rgba(180,130,10,.5));}}
@keyframes fade-glow{0%,100%{opacity:.6;}50%{opacity:1;}}
.ld-sub-row{display:flex;align-items:center;justify-content:center;gap:14px;margin-top:6px;flex-wrap:wrap;}
.ld-period{font-size:clamp(10px,1.1vw,14px);color:#4a3d24;font-weight:600;letter-spacing:2px;}
.ld-clock{font-size:clamp(10px,1.1vw,14px);color:#8a6d00;font-weight:700;letter-spacing:2px;font-variant-numeric:tabular-nums;}
.ld-pool{display:inline-flex;align-items:baseline;gap:8px;margin-top:8px;padding:6px 22px;border:1px solid rgba(184,134,11,.4);
  border-radius:100px;background:rgba(255,253,247,.7);box-shadow:0 2px 10px rgba(0,0,0,.05);}
.ld-pool-label{font-size:clamp(9px,1vw,12px);letter-spacing:3px;color:#8a6d00;font-weight:700;text-transform:uppercase;}
.ld-pool-amt{font-family:'Cinzel Decorative','Georgia',serif;font-weight:900;font-size:clamp(16px,2.2vw,30px);color:#9a7300;
  text-shadow:0 1px 4px rgba(120,90,10,.25);}
.ld-iphone-bg{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);z-index:3;
  height:min(88vh,820px);pointer-events:none;opacity:.16;
  filter:drop-shadow(0 0 80px rgba(218,165,32,.6));animation:iphone-bg-pulse 5s ease-in-out infinite;}
.ld-iphone-bg img{height:100%;width:auto;display:block;}
@keyframes iphone-bg-pulse{0%,100%{opacity:.13;}50%{opacity:.2;}}
.ld-body{position:relative;z-index:10;flex:1 1 auto;display:flex;flex-direction:column;min-height:0;padding:0 clamp(14px,2.4vw,40px);}
.ld-podium{display:grid;grid-template-columns:1fr 1.18fr 1fr;gap:clamp(8px,1vw,18px);align-items:end;
  margin:clamp(6px,1.2vh,16px) auto clamp(10px,1.6vh,20px);max-width:1100px;width:100%;flex:0 0 auto;}
.ld-pcard{position:relative;border-radius:18px;padding:clamp(10px,1.4vw,20px) clamp(8px,1.2vw,16px) clamp(12px,1.6vw,20px);
  text-align:center;background:linear-gradient(180deg,#fffdf7,#f8f1de);
  border:1px solid rgba(184,134,11,.35);box-shadow:0 10px 30px rgba(0,0,0,.1);}
.ld-pcard--1{order:2;transform:translateY(-14px);border-color:#DAA520;box-shadow:0 0 40px rgba(218,165,32,.25),0 10px 30px rgba(0,0,0,.1);}
.ld-pcard--2{order:1;}
.ld-pcard--3{order:3;}
.ld-pmedal{font-size:clamp(26px,3.6vw,52px);line-height:1;filter:drop-shadow(0 3px 6px rgba(0,0,0,.18));animation:trophy-float 3s ease-in-out infinite;}
@keyframes trophy-float{0%,100%{transform:translateY(0);}50%{transform:translateY(-6px);}}
.ld-pname{font-weight:800;font-size:clamp(12px,1.5vw,20px);margin-top:6px;letter-spacing:.3px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#1a1408;}
.ld-psub{font-size:clamp(9px,.95vw,12px);color:#4a3d24;font-weight:600;margin-top:2px;}
.ld-psub--bold{font-weight:800;color:#9a7300;font-size:clamp(10px,1.05vw,13px);}
.ld-ppts{font-family:'Cinzel Decorative','Georgia',serif;font-weight:900;color:#9a7300;margin-top:8px;
  font-size:clamp(20px,3vw,40px);text-shadow:0 1px 4px rgba(120,90,10,.2);}
.ld-ppts-label{font-size:clamp(8px,.85vw,11px);color:#8a6d00;font-weight:700;letter-spacing:2px;text-transform:uppercase;}
.ld-pts-wrap{position:relative;display:inline-block;}
.ld-pts-delta{position:absolute;left:50%;top:-2px;transform:translate(-50%,0);white-space:nowrap;
  font-family:'Cinzel','Georgia',serif;font-weight:800;font-size:.55em;color:#0b7a43;
  animation:pts-float-up 1.4s ease-out forwards;pointer-events:none;}
@keyframes pts-float-up{0%{opacity:0;transform:translate(-50%,6px) scale(.9);}
  15%{opacity:1;transform:translate(-50%,-8px) scale(1.05);}
  100%{opacity:0;transform:translate(-50%,-30px) scale(1);}}
.ld-pprize{margin-top:8px;font-size:clamp(11px,1.3vw,17px);font-weight:700;color:#0b7a43;}
.ld-crown{font-size:clamp(16px,2vw,26px);filter:drop-shadow(0 2px 4px rgba(184,134,11,.4));margin-left:4px;vertical-align:middle;}
.ld-progress{margin-top:6px;font-size:clamp(9px,1vw,12px);color:#8a6d00;font-weight:600;letter-spacing:.3px;}
.ld-progress b{color:#0b7a43;}
.ld-row-progress{flex:0 0 auto;width:clamp(90px,10vw,150px);text-align:right;font-size:clamp(9px,1vw,11px);color:#8a6d00;font-weight:600;white-space:nowrap;}
.ld-row-progress b{color:#0b7a43;}
.ld-empty-podium{opacity:.65;font-size:clamp(11px,1.2vw,15px);padding:20px 6px;color:#4a3d24;font-weight:600;}
.ld-list{flex:1 1 auto;min-height:0;max-width:1100px;margin:0 auto;width:100%;display:flex;flex-direction:column;gap:clamp(5px,.9vh,9px);
  padding-bottom:clamp(4px,1vh,10px);overflow:hidden;}
.ld-row{display:flex;align-items:center;gap:clamp(8px,1.2vw,18px);background:rgba(255,253,247,.75);
  border:1px solid rgba(184,134,11,.22);border-radius:12px;padding:clamp(6px,.9vh,11px) clamp(10px,1.4vw,18px);
  box-shadow:0 2px 8px rgba(0,0,0,.04);animation:row-in .4s ease both;}
.ld-row--empty{opacity:.7;}
.ld-row--empty .ld-row-name b{font-weight:700;color:#4a3d24;}
@keyframes row-in{from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:translateY(0);}}
.ld-row-rank{width:clamp(24px,2.4vw,36px);text-align:center;font-weight:800;font-size:clamp(13px,1.5vw,19px);color:#8a6d00;flex:0 0 auto;}
.ld-row-name{flex:1 1 auto;min-width:0;}
.ld-row-name b{font-size:clamp(12px,1.4vw,17px);color:#1a1408;}
.ld-live-badge{display:inline-flex;align-items:center;gap:4px;margin-left:8px;padding:1px 8px;border-radius:100px;
  background:rgba(255,60,60,.1);border:1px solid rgba(220,50,50,.45);color:#d63a3a;font-size:clamp(8px,.85vw,11px);
  letter-spacing:1px;vertical-align:middle;}
.ld-live-badge .ld-live-dot{width:5px;height:5px;}
.ld-row-sub{font-size:clamp(9px,.9vw,12px);color:#4a3d24;font-weight:600;margin-top:1px;}
.ld-row-sub--bold{font-weight:800;color:#9a7300;font-size:clamp(10px,1vw,13px);}
.ld-row-pts{flex:0 0 auto;text-align:right;font-weight:800;color:#9a7300;font-size:clamp(13px,1.5vw,18px);white-space:nowrap;}
.ld-row-prize{flex:0 0 auto;width:clamp(70px,8vw,110px);text-align:right;font-size:clamp(10px,1.1vw,14px);color:#0b7a43;font-weight:700;white-space:nowrap;}
.ld-page-dots{display:flex;justify-content:center;gap:6px;padding:4px 0 0;}
.ld-page-dot{width:6px;height:6px;border-radius:50%;background:rgba(138,109,0,.3);}
.ld-page-dot.active{background:#8a6d00;}
.ld-empty-list{text-align:center;color:#4a3d24;font-weight:600;padding:24px 0;font-size:clamp(11px,1.2vw,15px);}
.ld-footer{position:relative;z-index:10;flex:0 0 auto;padding:clamp(6px,1vh,12px) 0 clamp(8px,1.2vh,14px);overflow:hidden;}
.ld-status-row{display:flex;align-items:center;justify-content:center;gap:18px;margin-top:8px;font-size:clamp(9px,1vw,12px);color:#4a3d24;font-weight:600;letter-spacing:2px;}
.ld-live{display:inline-flex;align-items:center;gap:6px;color:#d63a3a;}
.ld-live-dot{width:7px;height:7px;border-radius:50%;background:#e03333;box-shadow:0 0 8px rgba(224,51,51,.6);animation:live-blink 1.2s step-start infinite;}
@keyframes live-blink{0%,100%{opacity:1;}50%{opacity:0;}}
.ld-flash{position:fixed;inset:0;z-index:8;pointer-events:none;background:rgba(218,165,32,.22);animation:flash-out 1s ease-out forwards;}
@keyframes flash-out{0%{opacity:1;}100%{opacity:0;}}
.ld-bang-wrap{position:fixed;inset:0;z-index:20;display:flex;align-items:center;justify-content:center;pointer-events:none;}
.ld-bang-box{background:rgba(255,253,247,.96);border:3px solid #B8860B;border-radius:22px;padding:22px 44px;text-align:center;
  backdrop-filter:blur(14px);box-shadow:0 0 60px rgba(184,134,11,.3),0 20px 60px rgba(0,0,0,.2);animation:bang-in .55s cubic-bezier(.34,1.56,.64,1);max-width:90vw;}
.ld-bang-line1{font-family:'Cinzel Decorative','Georgia',serif;font-size:clamp(24px,5vw,64px);color:#B8860B;font-weight:900;line-height:1.2;text-shadow:0 2px 8px rgba(120,90,10,.3);}
.ld-bang-line2{font-size:clamp(12px,1.8vw,20px);color:#8a6d00;font-weight:700;letter-spacing:4px;margin-top:8px;}
@keyframes bang-in{0%{transform:scale(.25) rotate(-8deg);opacity:0;}60%{transform:scale(1.07) rotate(2deg);}100%{transform:scale(1) rotate(0deg);opacity:1;}}
.ld-bang-box--day{animation:bang-in .55s cubic-bezier(.34,1.56,.64,1),bang-pulse 1.6s ease-in-out .55s infinite;
  border-color:#FFD700;}
@keyframes bang-pulse{0%,100%{box-shadow:0 0 60px rgba(255,215,0,.35),0 20px 60px rgba(0,0,0,.2);transform:scale(1);}
  50%{box-shadow:0 0 100px rgba(255,215,0,.65),0 20px 70px rgba(0,0,0,.25);transform:scale(1.025);}}
.ld-bang-line1--day{animation:line-shimmer 2.2s ease-in-out infinite;}
.ld-bang-box--contest{max-width:min(94vw,720px);pointer-events:auto;}
.ld-winner-list{margin-top:16px;display:flex;flex-direction:column;gap:6px;max-height:52vh;overflow-y:auto;text-align:left;}
.ld-winner-row{display:flex;align-items:center;gap:12px;padding:7px 14px;border-radius:10px;
  background:rgba(184,134,11,.08);border:1px solid rgba(184,134,11,.2);}
.ld-winner-rank{flex:0 0 auto;width:34px;text-align:center;font-weight:800;font-size:clamp(13px,1.6vw,18px);color:#8a6d00;}
.ld-winner-name{flex:1 1 auto;min-width:0;font-weight:700;font-size:clamp(12px,1.5vw,17px);color:#1a1408;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.ld-winner-prize{flex:0 0 auto;font-weight:800;font-size:clamp(12px,1.5vw,17px);color:#0b7a43;white-space:nowrap;}
.ld-winner-more{text-align:center;padding:6px 0 0;font-size:clamp(10px,1.1vw,13px);color:#8a6d00;font-weight:600;letter-spacing:1px;}
@keyframes line-shimmer{0%,100%{text-shadow:0 2px 8px rgba(120,90,10,.3);}50%{text-shadow:0 2px 24px rgba(255,180,0,.8);}}

/* ── Milestone "Coming Soon" landing — shown until MILESTONE_START, then this whole
   block just stops rendering and the normal live board (above) takes over automatically,
   since it's driven by the same ticking clock. Deliberately its own dark, spotlight-style
   teaser theme (not the gold/parchment live board) -- mystery/anticipation, not information
   density: only a few things on screen at once, winners and prizes reveal themselves one
   at a time rather than as a dense list/table. ── */
.ld-root--msland{background:
    radial-gradient(ellipse 900px 700px at 50% 10%,rgba(140,60,220,.28),transparent 60%),
    radial-gradient(ellipse 700px 600px at 10% 85%,rgba(255,61,129,.2),transparent 55%),
    radial-gradient(ellipse 700px 600px at 90% 80%,rgba(0,194,168,.18),transparent 55%),
    linear-gradient(180deg,#0c0714 0%,#050308 55%,#000 100%);
  color:#fff;}
.ld-root--msland .ld-corner{display:none;}
.ld-land{position:relative;z-index:10;flex:1 1 auto;display:flex;flex-direction:column;align-items:center;
  justify-content:center;padding:0 clamp(16px,4vw,48px);min-height:0;overflow:hidden;text-align:center;}
.ld-land-tagline{font-size:clamp(11px,1.5vw,16px);letter-spacing:7px;text-transform:uppercase;font-weight:800;
  background:linear-gradient(90deg,#FF3D81,#FFD700,#00E5C7,#FF3D81);background-size:300%;-webkit-background-clip:text;
  background-clip:text;-webkit-text-fill-color:transparent;animation:land-rainbow 5s linear infinite;margin-bottom:6px;}
@keyframes land-rainbow{to{background-position:300% center;}}
.ld-land-badge{display:inline-flex;align-items:center;gap:8px;padding:7px 24px;border-radius:100px;
  background:linear-gradient(90deg,#FF3D81,#FF9F0A,#FFD700);color:#fff;font-weight:800;font-size:clamp(10px,1.2vw,13px);
  letter-spacing:3px;text-transform:uppercase;box-shadow:0 4px 30px rgba(255,61,129,.5);animation:land-badge-pulse 2s ease-in-out infinite;}
@keyframes land-badge-pulse{0%,100%{transform:scale(1);}50%{transform:scale(1.06);}}
.ld-land-title{font-family:'Cinzel Decorative','Georgia',serif;font-weight:900;text-align:center;
  font-size:clamp(30px,7vw,84px);line-height:1.05;margin-top:16px;text-wrap:balance;color:#fff;
  text-shadow:0 0 40px rgba(255,215,0,.35),0 0 90px rgba(140,60,220,.3);animation:title-breathe 3s ease-in-out infinite;}
@keyframes title-breathe{0%,100%{filter:brightness(1);}50%{filter:brightness(1.18);}}
.ld-land-sub{text-align:center;font-size:clamp(12px,1.6vw,18px);color:rgba(255,255,255,.55);font-weight:600;margin-top:10px;letter-spacing:.5px;}
.ld-countdown{display:flex;gap:clamp(8px,1.6vw,20px);margin:clamp(20px,3.2vh,36px) 0 clamp(10px,1.8vh,20px);flex-wrap:wrap;justify-content:center;flex:0 0 auto;}
.ld-cd-box{position:relative;min-width:clamp(64px,9vw,108px);padding:clamp(10px,1.6vw,20px) clamp(8px,1.2vw,14px);
  border-radius:18px;text-align:center;background:rgba(255,255,255,.05);backdrop-filter:blur(10px);
  border:2px solid transparent;box-shadow:0 10px 40px rgba(0,0,0,.5);animation:cd-pop .5s ease both;}
.ld-cd-box:nth-child(1){border-color:#FF3D81;box-shadow:0 0 30px rgba(255,61,129,.4),0 10px 30px rgba(0,0,0,.3);}
.ld-cd-box:nth-child(2){border-color:#FF9F0A;box-shadow:0 0 30px rgba(255,159,10,.4),0 10px 30px rgba(0,0,0,.3);}
.ld-cd-box:nth-child(3){border-color:#00E5C7;box-shadow:0 0 30px rgba(0,229,199,.4),0 10px 30px rgba(0,0,0,.3);}
.ld-cd-box:nth-child(4){border-color:#4FA0FF;box-shadow:0 0 30px rgba(79,160,255,.4),0 10px 30px rgba(0,0,0,.3);}
@keyframes cd-pop{from{transform:scale(.8);opacity:0;}to{transform:scale(1);opacity:1;}}
.ld-cd-num{font-family:'Cinzel Decorative',serif;font-weight:900;font-size:clamp(26px,4.6vw,52px);color:#fff;
  font-variant-numeric:tabular-nums;line-height:1;text-shadow:0 0 20px currentColor;}
.ld-cd-label{font-size:clamp(8px,.9vw,11px);letter-spacing:2px;text-transform:uppercase;color:rgba(255,255,255,.5);font-weight:700;margin-top:4px;}

/* One prize revealed at a time, not the full tier ladder -- cycles every few seconds. */
.ld-prize-teaser{flex:0 0 auto;}
.ld-prize-teaser-label{font-size:clamp(10px,1.2vw,13px);letter-spacing:3px;text-transform:uppercase;color:rgba(255,255,255,.45);font-weight:700;}
.ld-prize-teaser-amt{font-family:'Cinzel Decorative',serif;font-weight:900;font-size:clamp(32px,6vw,68px);margin-top:4px;
  background:linear-gradient(90deg,#FFD700,#FFEC8B,#FFD700);-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;
  filter:drop-shadow(0 0 30px rgba(255,215,0,.5));animation:prize-swap .6s cubic-bezier(.34,1.56,.64,1);}
@keyframes prize-swap{from{opacity:0;transform:translateY(14px) scale(.88);}to{opacity:1;transform:translateY(0) scale(1);}}
.ld-prize-teaser-pool{margin-top:6px;font-size:clamp(11px,1.4vw,14px);color:rgba(255,255,255,.5);font-weight:700;letter-spacing:1px;}
.ld-prize-teaser-pool b{color:#FFD700;}

/* Winners float freely across the whole screen, a few at a time, each with a different
   drift pattern -- never the full list at once. */
.ld-floaters{position:fixed;inset:0;z-index:3;pointer-events:none;overflow:hidden;}
.ld-floater{position:absolute;display:flex;flex-direction:column;align-items:flex-start;gap:5px;padding:12px 22px;border-radius:22px;
  background:rgba(255,255,255,.06);backdrop-filter:blur(8px);border:1px solid rgba(255,255,255,.16);
  max-width:min(40vw,400px);white-space:normal;box-shadow:0 10px 30px rgba(0,0,0,.5);}
.ld-floater-tag{font-size:clamp(10px,1vw,12.5px);font-weight:800;letter-spacing:.14em;text-transform:uppercase;color:#FFD700;opacity:.85;white-space:nowrap;}
.ld-floater-body{display:flex;flex-wrap:wrap;align-items:center;gap:10px;font-size:clamp(16px,1.9vw,22px);font-weight:800;color:#fff;}
.ld-floater-rank{color:#FFD700;}
.ld-floater-name{color:#fff;}
.ld-floater-pts{color:rgba(255,255,255,.65);font-weight:600;}
.ld-floater-prize{color:#4ADE80;}
.ld-floater--rise{animation-name:float-rise;}
.ld-floater--drift{animation-name:float-drift;}
.ld-floater--pulse{animation-name:float-pulse;}
.ld-floater--diag{animation-name:float-diag;}
.ld-floater{animation-timing-function:ease-in-out;animation-fill-mode:forwards;}
@keyframes float-rise{0%{opacity:0;transform:translateY(50px);}12%{opacity:1;}82%{opacity:1;}100%{opacity:0;transform:translateY(-280px);}}
@keyframes float-drift{0%{opacity:0;transform:translateX(-70px);}12%{opacity:1;}82%{opacity:1;}100%{opacity:0;transform:translateX(70px);}}
@keyframes float-pulse{0%{opacity:0;transform:scale(.82);}14%{opacity:1;transform:scale(1);}80%{opacity:1;}100%{opacity:0;transform:scale(1.08);}}
@keyframes float-diag{0%{opacity:0;transform:translate(-50px,50px) rotate(-4deg);}12%{opacity:1;}82%{opacity:1;}100%{opacity:0;transform:translate(60px,-240px) rotate(4deg);}}

/* ── Daily / iPhone board — a deliberately different, light Apple-style theme:
   flat white cards, blue accent, system sans-serif — instead of the hourly
   board's gold/parchment casino look. ── */
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800;900&display=swap');
.ld-root--daily{background:radial-gradient(ellipse at 30% 0%,#ffffff 0%,#f5f5f7 55%,#eef1f5 100%);
  font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#1d1d1f;}
.ld-root--daily .ld-orb-1{background:radial-gradient(circle,rgba(0,113,227,.14) 0%,transparent 65%);}
.ld-root--daily .ld-orb-2{background:radial-gradient(circle,rgba(191,90,242,.10) 0%,transparent 65%);}
.ld-root--daily .ld-corner{display:none;}
.ld-root--daily .ld-qr-panel{border-color:rgba(0,113,227,.35);box-shadow:0 0 16px rgba(0,113,227,.14),0 8px 24px rgba(0,0,0,.08);}
.ld-root--daily .ld-club{color:#86868b;letter-spacing:4px;font-weight:600;}
.ld-root--daily .ld-title{font-family:'Inter',sans-serif;font-weight:900;letter-spacing:-.5px;
  background:linear-gradient(90deg,#0a2540,#0071e3,#42a5ff,#0071e3,#0a2540);background-size:300%;
  -webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;
  animation:shimmer 4s linear infinite,title-glow-blue 2.5s ease-in-out infinite;}
@keyframes title-glow-blue{0%,100%{filter:drop-shadow(0 2px 4px rgba(0,113,227,.15));}50%{filter:drop-shadow(0 2px 12px rgba(0,113,227,.4));}}
.ld-root--daily .ld-period,.ld-root--daily .ld-clock{color:#515154;}
.ld-root--daily .ld-pool{border-color:rgba(0,113,227,.3);background:rgba(255,255,255,.75);box-shadow:0 2px 12px rgba(0,113,227,.08);}
.ld-root--daily .ld-pool-label{color:#86868b;}
.ld-root--daily .ld-pool-amt{font-family:'Inter',sans-serif;color:#0071e3;text-shadow:none;}
.ld-root--daily .ld-iphone-bg{filter:drop-shadow(0 0 70px rgba(0,113,227,.28));}
.ld-root--daily .ld-pcard{background:#ffffff;border:1px solid rgba(0,0,0,.06);box-shadow:0 10px 30px rgba(0,20,60,.06);}
.ld-root--daily .ld-pcard--1{border-color:rgba(0,113,227,.4);box-shadow:0 0 40px rgba(0,113,227,.16),0 10px 30px rgba(0,20,60,.08);}
.ld-root--daily .ld-pname{color:#1d1d1f;}
.ld-root--daily .ld-psub,.ld-root--daily .ld-ppts-label{color:#86868b;}
.ld-root--daily .ld-ppts{font-family:'Inter',sans-serif;color:#0071e3;text-shadow:none;}
.ld-root--daily .ld-pprize{color:#0071e3;}
.ld-root--daily .ld-empty-podium{color:#86868b;}
.ld-root--daily .ld-row{background:#ffffff;border-color:rgba(0,0,0,.06);box-shadow:0 2px 8px rgba(0,20,60,.04);}
.ld-root--daily .ld-row--empty .ld-row-name b{color:#86868b;}
.ld-root--daily .ld-row-rank{color:#0071e3;}
.ld-root--daily .ld-row-name b{color:#1d1d1f;}
.ld-root--daily .ld-row-sub{color:#86868b;}
.ld-root--daily .ld-row-pts{color:#0071e3;}
.ld-root--daily .ld-page-dot{background:rgba(0,113,227,.25);}
.ld-root--daily .ld-page-dot.active{background:#0071e3;}
.ld-root--daily .ld-empty-list{color:#86868b;}
.ld-root--daily .ld-status-row{color:#86868b;}
.ld-root--daily .ld-toc{display:flex;align-items:center;justify-content:center;gap:10px;flex-wrap:wrap;
  margin-top:6px;font-size:clamp(8px,.85vw,11px);color:#86868b;font-weight:600;letter-spacing:.3px;}
.ld-root--daily .ld-toc-sep{color:rgba(0,113,227,.4);}
.ld-root--daily .ld-flash{background:rgba(0,113,227,.16);}
.ld-root--daily .ld-bang-box{background:rgba(255,255,255,.97);border-color:#0071e3;box-shadow:0 0 60px rgba(0,113,227,.25),0 20px 60px rgba(0,20,60,.15);}
.ld-root--daily .ld-bang-line1{font-family:'Inter',sans-serif;color:#0071e3;text-shadow:none;}
.ld-root--daily .ld-bang-line2{color:#515154;}
.ld-root--daily .ld-bang-box--day{border-color:#0071e3;}
@keyframes bang-pulse-blue{0%,100%{box-shadow:0 0 60px rgba(0,113,227,.3),0 20px 60px rgba(0,20,60,.15);transform:scale(1);}
  50%{box-shadow:0 0 100px rgba(0,113,227,.55),0 20px 70px rgba(0,20,60,.2);transform:scale(1.025);}}
.ld-root--daily .ld-bang-box--day{animation:bang-in .55s cubic-bezier(.34,1.56,.64,1),bang-pulse-blue 1.6s ease-in-out .55s infinite;}
.ld-root--daily .ld-bang-line1--day{animation:line-shimmer-blue 2.2s ease-in-out infinite;}
@keyframes line-shimmer-blue{0%,100%{text-shadow:0 2px 8px rgba(0,113,227,.2);}50%{text-shadow:0 2px 24px rgba(0,113,227,.6);}}
`;

function useLiveClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

// Smoothly counts up to a player's new point total and pops a "+X" badge
// whenever it rises — makes live scoring changes visible instead of a silent jump.
function AnimatedPoints({ id, value, decimals = 1 }) {
  const [display, setDisplay] = useState(value);
  const [flash, setFlash] = useState(null); // { key, delta }
  const prevValueRef = useRef(value);
  const prevIdRef = useRef(id);
  const rafRef = useRef(null);
  const flashSeq = useRef(0);

  useEffect(() => {
    const idChanged = prevIdRef.current !== id;
    const from = idChanged ? value : prevValueRef.current;
    const to = value;
    prevIdRef.current = id;

    if (idChanged || from === to) {
      setDisplay(to);
      prevValueRef.current = to;
      return;
    }

    if (to > from) {
      flashSeq.current += 1;
      setFlash({ key: flashSeq.current, delta: to - from });
    }

    const start = performance.now();
    const duration = 700;
    cancelAnimationFrame(rafRef.current);
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(from + (to - from) * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
      else prevValueRef.current = to;
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [id, value]);

  return (
    <span className="ld-pts-wrap">
      {fmtPts(display, decimals)}
      {flash && <span key={flash.key} className="ld-pts-delta">+{fmtPts(flash.delta, decimals)}</span>}
    </span>
  );
}

export default function LeaderboardDisplay({ variant = "contest" }) {
  const cfg = VARIANTS[variant];
  const [rankings, setRankings] = useState([]);
  const [period, setPeriod] = useState(null);
  const [page, setPage] = useState(0);
  const [celebrating, setCelebrating] = useState(false);
  const [bangVisible, setBangVisible] = useState(false);
  const [bangName, setBangName] = useState("");
  const [dayWinnerMode, setDayWinnerMode] = useState(false);
  const [contestWinnerMode, setContestWinnerMode] = useState(false);
  const [contestWinners, setContestWinners] = useState([]);
  const [contestEnded, setContestEnded] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [previousWinners, setPreviousWinners] = useState([]);
  const [floaters, setFloaters] = useState([]);
  const [prizeTeaserIndex, setPrizeTeaserIndex] = useState(0);
  const floaterSeq = useRef(0);
  const winnerCursorRef = useRef(0);

  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const starsRef = useRef([]);
  const rafRef = useRef(null);
  const prevLeaderRef = useRef(undefined);
  const listRef = useRef(null);
  const firstRowRef = useRef(null);
  const [pageSize, setPageSize] = useState(6);
  const currentDayRef = useRef(todayIST());
  const dayWinnerIntervalRef = useRef(null);
  const dayWinnerTimeoutRef = useRef(null);
  const contestEndedRef = useRef(false); // fires the contest-end celebration exactly once
  const now = useLiveClock();

  // ── inject CSS + font ──
  useEffect(() => {
    const style = document.createElement("style");
    style.id = "ld-css";
    style.textContent = CSS;
    document.head.appendChild(style);
    return () => document.getElementById("ld-css")?.remove();
  }, []);

  // ── stars ──
  useEffect(() => {
    starsRef.current = Array.from({ length: 160 }, () => ({
      x: Math.random(), y: Math.random(),
      r: Math.random() * 1.6 + 0.3,
      a: Math.random(),
      da: (Math.random() * 0.01 + 0.002) * (Math.random() > 0.5 ? 1 : -1),
    }));
  }, []);

  // ── canvas loop ──
  useEffect(() => {
    const cv = canvasRef.current;
    const ctx = cv.getContext("2d");
    const resize = () => { cv.width = window.innerWidth; cv.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);
    const tick = () => {
      ctx.clearRect(0, 0, cv.width, cv.height);
      starsRef.current.forEach((s) => {
        s.a += s.da;
        if (s.a <= 0 || s.a >= 1) s.da *= -1;
        ctx.save();
        ctx.globalAlpha = Math.max(0, Math.min(1, s.a)) * 0.8;
        ctx.fillStyle = isIphoneVariant(variant) ? "#0071e3" : "#c9a227";
        ctx.beginPath();
        ctx.arc(s.x * cv.width, s.y * cv.height, s.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });
      particlesRef.current = particlesRef.current.filter((p) => p.a > 0.01);
      for (const p of particlesRef.current) {
        p.x += p.vx; p.y += p.vy;
        p.vy += p.grav ?? 0.15;
        p.vx *= p.drag ?? 0.99;
        if (p.rV) p.r0 = (p.r0 || 0) + p.rV;
        p.a -= p.dec ?? 0.01;
        ctx.save();
        ctx.globalAlpha = Math.max(0, p.a);
        ctx.fillStyle = p.color;
        if (p.isRect) {
          ctx.translate(p.x, p.y);
          ctx.rotate(p.r0 || 0);
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        } else {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.sz ?? 3, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();
    return () => { window.removeEventListener("resize", resize); cancelAnimationFrame(rafRef.current); };
  }, []);

  const spawnConfetti = useCallback((count = 300, palette = COLORS) => {
    const ps = [];
    for (let i = 0; i < count; i++) {
      ps.push({
        isRect: true,
        x: Math.random() * window.innerWidth,
        y: -30 - Math.random() * 400,
        vx: (Math.random() - 0.5) * 10,
        vy: Math.random() * 4 + 1.5,
        r0: Math.random() * Math.PI * 2,
        rV: (Math.random() - 0.5) * 0.22,
        w: Math.random() * 14 + 6,
        h: Math.random() * 8 + 4,
        color: palette[~~(Math.random() * palette.length)],
        a: 1, grav: 0.09, drag: 0.99, dec: 0.0026,
      });
    }
    particlesRef.current.push(...ps);
  }, []);

  const spawnFirework = useCallback((x, y, palette = COLORS, size = 100) => {
    const ps = [];
    for (let i = 0; i < size; i++) {
      const ang = (i / size) * Math.PI * 2;
      const spd = Math.random() * 11 + 4;
      ps.push({
        x, y, vx: Math.cos(ang) * spd, vy: Math.sin(ang) * spd,
        sz: Math.random() * 4 + 2, color: palette[~~(Math.random() * palette.length)],
        a: 1, grav: 0.06, drag: 0.95, dec: 0.013,
      });
    }
    particlesRef.current.push(...ps);
  }, []);

  const burstFireworks = useCallback((palette = COLORS) => {
    const spots = [
      [window.innerWidth * 0.15, window.innerHeight * 0.25],
      [window.innerWidth * 0.85, window.innerHeight * 0.25],
      [window.innerWidth * 0.5, window.innerHeight * 0.15],
      [window.innerWidth * 0.3, window.innerHeight * 0.45],
      [window.innerWidth * 0.7, window.innerHeight * 0.45],
    ];
    spots.forEach(([x, y], i) => setTimeout(() => spawnFirework(x, y, palette), i * 130));
  }, [spawnFirework]);

  // Bigger, wider, more colorful barrage reserved for the once-a-day winner reveal.
  const burstFireworksBig = useCallback(() => {
    const w = window.innerWidth, h = window.innerHeight;
    const spots = [
      [w * 0.08, h * 0.55], [w * 0.92, h * 0.55],
      [w * 0.18, h * 0.22], [w * 0.82, h * 0.22],
      [w * 0.5, h * 0.12], [w * 0.5, h * 0.65],
      [w * 0.32, h * 0.4], [w * 0.68, h * 0.4],
      [w * 0.5, h * 0.35],
    ];
    spots.forEach(([x, y], i) => setTimeout(() => spawnFirework(x, y, DAY_WINNER_COLORS, 140), i * 90));
  }, [spawnFirework]);

  const celebrate = useCallback((name) => {
    const palette = isIphoneVariant(variant) ? DAILY_LEADER_COLORS : COLORS;
    setBangName(name);
    setDayWinnerMode(false);
    setCelebrating(true);
    setBangVisible(true);
    setTimeout(() => setCelebrating(false), 5000);
    setTimeout(() => setBangVisible(false), 4500);
    spawnConfetti(300, palette);
    burstFireworks(palette);
  }, [spawnConfetti, burstFireworks, variant]);

  // ── declare the day's final winner at the midnight IST rollover: a full minute of
  // continuous, colorful confetti/fireworks before the fresh new day's board takes over ──
  const celebrateDayWinner = useCallback((name) => {
    clearInterval(dayWinnerIntervalRef.current);
    clearTimeout(dayWinnerTimeoutRef.current);
    setBangName(name);
    setDayWinnerMode(true);
    setCelebrating(true);
    setBangVisible(true);
    spawnConfetti(550, DAY_WINNER_COLORS);
    burstFireworksBig();
    dayWinnerIntervalRef.current = setInterval(() => {
      spawnConfetti(550, DAY_WINNER_COLORS);
      burstFireworksBig();
    }, 1600);
    dayWinnerTimeoutRef.current = setTimeout(() => {
      clearInterval(dayWinnerIntervalRef.current);
      setCelebrating(false);
      setBangVisible(false);
      setDayWinnerMode(false);
    }, 60000);
  }, [spawnConfetti, burstFireworksBig]);

  useEffect(() => () => {
    clearInterval(dayWinnerIntervalRef.current);
    clearTimeout(dayWinnerTimeoutRef.current);
  }, []);

  // ── watch the live clock for the midnight IST rollover (daily variant only) ──
  useEffect(() => {
    if (variant !== "daily") return;
    const nowDay = todayIST();
    if (nowDay === currentDayRef.current) return;
    const endedDay = currentDayRef.current;
    currentDayRef.current = nowDay;
    (async () => {
      try {
        const res = await axiosRequest("GET", `${process.env.REACT_APP_API_URL}/leaderboard/rankings/daily/public?date=${endedDay}`, null);
        const winner = res?.data?.rankings?.[0];
        if (winner) celebrateDayWinner(winner._id);
      } catch {}
    })();
  }, [now, variant, celebrateDayWinner]);

  // ── watch the live clock for the Sunday 23:59:59 -> Monday 00:00 IST rollover
  // (weekend variant only): the Fri-Sun window's winner is decided at that exact moment,
  // not every midnight like the daily board -- Mon-Thu keep showing that same frozen
  // result until the next Friday resets it. ──
  useEffect(() => {
    if (variant !== "weekend") return;
    const nowDay = todayIST();
    if (nowDay === currentDayRef.current) return;
    const endedDay = currentDayRef.current; // the Sunday that just ended
    currentDayRef.current = nowDay;
    // Only the rollover past the window's last day decides the weekend -- normally Sun ->
    // Mon, but an extended weekend (see WEEKEND_EXTENSIONS in the backend) ends later.
    // `period` is still the just-ended window here; the next poll moves it on.
    const lastDay = period?.end ? istDateOf(period.end) : null;
    if (lastDay ? endedDay !== lastDay : istDayOfWeek(nowDay) !== 1) return;
    (async () => {
      try {
        const res = await axiosRequest("GET", `${process.env.REACT_APP_API_URL}/leaderboard/rankings/weekend/public?date=${endedDay}`, null);
        const winner = res?.data?.rankings?.[0];
        if (winner) celebrateDayWinner(winner._id);
      } catch {}
    })();
  }, [now, variant, period, celebrateDayWinner]);

  // ── declare the contest's final winners once CONTEST_END passes: same full-minute
  // confetti/fireworks treatment as the daily winner, but listing every prize-winning
  // rank (with amounts) instead of a single name, since the contest pays out many ranks. ──
  const celebrateContestEnd = useCallback((winnersList) => {
    clearInterval(dayWinnerIntervalRef.current);
    clearTimeout(dayWinnerTimeoutRef.current);
    setContestWinners(winnersList);
    setContestWinnerMode(true);
    setCelebrating(true);
    setBangVisible(true);
    spawnConfetti(550, DAY_WINNER_COLORS);
    burstFireworksBig();
    dayWinnerIntervalRef.current = setInterval(() => {
      spawnConfetti(550, DAY_WINNER_COLORS);
      burstFireworksBig();
    }, 1600);
    dayWinnerTimeoutRef.current = setTimeout(() => {
      clearInterval(dayWinnerIntervalRef.current);
      setCelebrating(false);
      setBangVisible(false);
    }, 60000);
  }, [spawnConfetti, burstFireworksBig]);

  // ── watch the live clock for the promo window ending (contest + milestone only) ──
  useEffect(() => {
    if (variant !== "contest" && variant !== "milestone") return;
    if (contestEndedRef.current) return;
    if (!period?.end) return;
    if (now < new Date(period.end)) return;
    contestEndedRef.current = true;
    setContestEnded(true);
    const winners = variant === "milestone"
      ? rankings
          .filter((r) => r.current_tier)
          .map((r) => ({ rank: r.rank, name: r._id, prize: r.current_tier.prize }))
      : rankings
          .filter((r) => prizeForRank(r.rank, variant, tieGroupSizeFor(rankings, r.rank)))
          .map((r) => ({
            rank: r.rank,
            name: r._id,
            prize: prizeForRank(r.rank, variant, tieGroupSizeFor(rankings, r.rank)),
          }));
    celebrateContestEnd(winners);
  }, [now, variant, period, rankings, celebrateContestEnd]);

  // ── poll rankings ──
  useEffect(() => {
    const poll = async () => {
      try {
        const r = await axiosRequest("GET", `${process.env.REACT_APP_API_URL}${cfg.rankingsUrl}`, null);
        const data = r?.data;
        if (!data) return;
        const list = data.rankings || [];
        setRankings(list);
        setPeriod(data.period || null);
        setLastUpdated(new Date());

        const leaderId = list[0]?._id || null;
        if (prevLeaderRef.current !== undefined && leaderId && leaderId !== prevLeaderRef.current) {
          celebrate(leaderId);
        }
        prevLeaderRef.current = leaderId;
      } catch {}
    };
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => clearInterval(id);
  }, [celebrate]);

  // ── milestone landing (pre-start) only: fetch the just-ended contest's final standings
  // once, to show as the "previous winners" ticker while counting down to the new board. ──
  useEffect(() => {
    if (variant !== "milestone") return;
    (async () => {
      try {
        const res = await axiosRequest("GET", `${process.env.REACT_APP_API_URL}/leaderboard/rankings/public`, null);
        const list = res?.data?.rankings || [];
        const winners = list
          .filter((r) => prizeForRank(r.rank, "contest", tieGroupSizeFor(list, r.rank)))
          .map((r) => ({ rank: r.rank, name: r._id, points: r.total_points, prize: prizeForRank(r.rank, "contest", tieGroupSizeFor(list, r.rank)) }));
        setPreviousWinners(winners);
      } catch {}
    })();
  }, [variant]);

  // ── milestone landing only: gentle periodic confetti while counting down, so the
  // teaser page feels alive rather than static. Stops the instant the countdown ends. ──
  useEffect(() => {
    if (variant !== "milestone" || now >= MILESTONE_START) return;
    const id = setInterval(() => spawnConfetti(70, DAY_WINNER_COLORS), 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, now >= MILESTONE_START, spawnConfetti]);

  // ── milestone landing only: winners drift across the full screen one at a time
  // (never the whole list at once) -- a different float pattern each spawn, picked
  // round-robin through previousWinners so everyone eventually gets shown. ──
  const FLOAT_TYPES = ["rise", "drift", "pulse", "diag"];
  const FLOAT_DURATIONS = { rise: 7000, drift: 8000, pulse: 6000, diag: 7500 };
  useEffect(() => {
    if (variant !== "milestone" || now >= MILESTONE_START || previousWinners.length === 0) return;
    const spawn = () => {
      setFloaters((cur) => {
        if (cur.length >= 4) return cur; // keep the screen uncluttered
        const w = previousWinners[winnerCursorRef.current % previousWinners.length];
        winnerCursorRef.current += 1;
        const type = FLOAT_TYPES[~~(Math.random() * FLOAT_TYPES.length)];
        const id = ++floaterSeq.current;
        // Hug the left/right/bottom edges rather than scattering across the whole
        // canvas -- the countdown + title + prize teaser own the center column, so
        // floaters frame that content instead of drifting over it. The title itself
        // spans nearly full-width, so even edge positions must skip its vertical band.
        const edgeTop = () => (Math.random() < 0.5 ? 3 + Math.random() * 14 : 46 + Math.random() * 44);
        const zone = ["left", "right", "bottom"][~~(Math.random() * 3)];
        const pos = zone === "left"
          ? { top: `${edgeTop()}%`, left: `${2 + Math.random() * 11}%` }
          : zone === "right"
          ? { top: `${edgeTop()}%`, right: `${2 + Math.random() * 11}%` }
          : { top: `${86 + Math.random() * 4}%`, left: `${6 + Math.random() * 78}%` };
        const item = { id, winner: w, type, ...pos, duration: FLOAT_DURATIONS[type] };
        setTimeout(() => setFloaters((f) => f.filter((x) => x.id !== id)), item.duration + 200);
        return [...cur, item];
      });
    };
    spawn();
    const id = setInterval(spawn, 2200);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, now >= MILESTONE_START, previousWinners]);

  // ── milestone landing only: reveal one prize tier at a time (low -> high, looping)
  // instead of the full ladder, for a "building up to something big" reveal. ──
  useEffect(() => {
    if (variant !== "milestone" || now >= MILESTONE_START) return;
    const id = setInterval(() => setPrizeTeaserIndex((i) => (i + 1) % MILESTONE_TIERS.length), 2800);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, now >= MILESTONE_START]);

  const top3 = rankings.slice(0, 3);
  // Milestone has no fixed number of paying slots (every player is independently chasing
  // their own next tier), so it just lists whoever's actually playing -- no "awaiting
  // player" placeholders like the fixed-slot contest/daily boards use below.
  // Always show every prize-winning rank (4 through the last paying rank), even
  // as "awaiting player" placeholders, so the full winning zone stays visible.
  const lastPrizeRank = isIphoneVariant(variant) ? IPHONE_LIST_SIZE : PRIZE_TABLE[PRIZE_TABLE.length - 1].to;
  const rest = variant === "milestone"
    ? rankings.slice(3)
    : Array.from({ length: Math.max(0, lastPrizeRank - 3) }, (_, i) => rankings[i + 3] || null);
  const totalPages = Math.max(1, Math.ceil(rest.length / pageSize));
  const tieGroupSize = (rank) => tieGroupSizeFor(rankings, rank);
  const displayDecimals = computeDisplayDecimals(rankings);

  // ── auto-cycle rank list pages ──
  useEffect(() => {
    setPage(0);
    if (totalPages <= 1) return;
    const id = setInterval(() => setPage((p) => (p + 1) % totalPages), PAGE_CYCLE_MS);
    return () => clearInterval(id);
  }, [totalPages, rest.length]);

  // ── fit as many rows as actually fit the available height, instead of assuming a
  // fixed count — podium height varies with LIVE badges/long names, and screens vary
  // in aspect ratio, so a hardcoded row count silently clips the last row otherwise. ──
  useEffect(() => {
    const measure = () => {
      const container = listRef.current;
      const row = firstRowRef.current;
      if (!container || !row) return;
      const rowH = row.offsetHeight;
      if (rowH <= 0) return;
      const gap = parseFloat(getComputedStyle(container).rowGap || "0") || 0;
      const fit = Math.max(1, Math.floor((container.clientHeight + gap) / (rowH + gap)));
      setPageSize((prev) => (prev === fit ? prev : fit));
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (listRef.current) ro.observe(listRef.current);
    window.addEventListener("resize", measure);
    const id = setInterval(measure, 3000);
    return () => { ro.disconnect(); window.removeEventListener("resize", measure); clearInterval(id); };
  }, [rankings]);

  const pageRows = rest.slice(page * pageSize, page * pageSize + pageSize);
  const secondsAgo = lastUpdated ? Math.max(0, Math.round((now - lastUpdated) / 1000)) : null;
  const medal = ["🥇", "🥈", "🥉"];

  // ── milestone landing: show a countdown teaser until MILESTONE_START, then this
  // condition flips false on its own (driven by the same ticking clock) and the normal
  // live board below renders instead -- no navigation, no reload. ──
  const msLanding = variant === "milestone" && now < MILESTONE_START;
  const msCountdownMs = Math.max(0, MILESTONE_START - now);
  const msCountdown = {
    days: Math.floor(msCountdownMs / 86400000),
    hours: Math.floor((msCountdownMs % 86400000) / 3600000),
    minutes: Math.floor((msCountdownMs % 3600000) / 60000),
    seconds: Math.floor((msCountdownMs % 60000) / 1000),
  };

  if (msLanding) {
    const teaserTier = MILESTONE_TIERS[prizeTeaserIndex];
    return (
      <div className="ld-root ld-root--msland">
        <canvas ref={canvasRef} className="ld-canvas" />
        <div className="ld-orb ld-orb-1" /><div className="ld-orb ld-orb-2" />

        <div className="ld-floaters">
          {floaters.map((f) => (
            <div
              key={f.id}
              className={`ld-floater ld-floater--${f.type}`}
              style={{ top: f.top, left: f.left, right: f.right, animationDuration: `${f.duration}ms` }}
            >
              <span className="ld-floater-tag">🏆 Previous Winner</span>
              <span className="ld-floater-body">
                <span className="ld-floater-rank">{f.winner.rank <= 3 ? medal[f.winner.rank - 1] : `#${f.winner.rank}`}</span>
                <span className="ld-floater-name">{f.winner.name}</span>
                <span className="ld-floater-pts">{fmtPts(f.winner.points, 0)} pts</span>
                <span className="ld-floater-prize">{fmtPrize(f.winner.prize)}</span>
              </span>
            </div>
          ))}
        </div>

        <div className="ld-land">
          <div className="ld-land-tagline">Something Big Is Coming</div>
          <div className="ld-land-badge">🎉 Coming Soon 🎉</div>
          <div className="ld-land-title">Milestone Leaderboard</div>
          <div className="ld-land-sub">21 July — 6 August winners are in · The next big one starts soon!</div>

          <div className="ld-countdown">
            <div className="ld-cd-box"><div className="ld-cd-num">{msCountdown.days}</div><div className="ld-cd-label">Days</div></div>
            <div className="ld-cd-box"><div className="ld-cd-num">{String(msCountdown.hours).padStart(2, "0")}</div><div className="ld-cd-label">Hours</div></div>
            <div className="ld-cd-box"><div className="ld-cd-num">{String(msCountdown.minutes).padStart(2, "0")}</div><div className="ld-cd-label">Minutes</div></div>
            <div className="ld-cd-box"><div className="ld-cd-num">{String(msCountdown.seconds).padStart(2, "0")}</div><div className="ld-cd-label">Seconds</div></div>
          </div>

          <div className="ld-prize-teaser">
            <div className="ld-prize-teaser-label">Reach {fmtPts(teaserTier.target, 0)} pts or {teaserTier.hours}h, win</div>
            <div className="ld-prize-teaser-amt" key={prizeTeaserIndex}>{fmtPrize(teaserTier.prize)}</div>
            <div className="ld-prize-teaser-pool">Total pool <b>{MILESTONE_POOL_LABEL}</b> · early bird 6–11:59 AM = 1.2× points</div>
          </div>
        </div>

        <footer className="ld-footer">
          <div className="ld-status-row"><span>🎊 Get ready — the leaderboard goes live automatically 🎊</span></div>
        </footer>
      </div>
    );
  }

  return (
    <div className={`ld-root${isIphoneVariant(variant) ? " ld-root--daily" : ""}`}>
      <canvas ref={canvasRef} className="ld-canvas" />
      <div className="ld-orb ld-orb-1" />
      <div className="ld-orb ld-orb-2" />
      <div className="ld-corner tl" /><div className="ld-corner tr" /><div className="ld-corner bl" /><div className="ld-corner br" />
      {isIphoneVariant(variant) && (
        <div className="ld-iphone-bg">
          <img src="/iphone17.png" alt="" />
        </div>
      )}
      <div className="ld-qr-panel">
        <img
          src={variant === "daily" ? "/daily-leaderboard-qr.jpg" : variant === "weekend" ? "/weekend-leaderboard-qr.png" : variant === "milestone" ? "/milestone-leaderboard-qr.png" : "/leaderboard-qr.png"}
          alt="Scan to check your ranking"
        />
      </div>

      <header className="ld-header">
        <div className="ld-club">{isIphoneVariant(variant) ? "📱 Real Gaming Club" : "♠ ♣ Real Gaming Club ♥ ♦"}</div>
        <div className="ld-title">{cfg.title}</div>
        <div className="ld-sub-row">
          {period && (
            variant === "daily"
              ? <span className="ld-period">Today · {fmtDateShort(period.start)}</span>
              : variant === "weekend"
              ? <span className="ld-period">{istDayOfWeek(istDateOf(period.end)) === 1 ? "Fri–Mon" : "Fri–Sun"} · {fmtDateShort(period.start)} – {fmtDateShort(period.end)}</span>
              : <span className="ld-period">{fmtDateShort(period.start)} — {fmtDateShort(period.end)}{contestEnded ? " · CONTEST ENDED" : ""}</span>
          )}
          <span className="ld-clock">{now.toLocaleTimeString("en-IN", { timeZone: IST_TZ, hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
        </div>
        <div className="ld-pool">
          {isIphoneVariant(variant) ? (
            <>
              <span className="ld-pool-label">{variant === "daily" ? "Today's" : "This Weekend's"} Winner Gets</span>
              <span className="ld-pool-amt">iPhone 17</span>
            </>
          ) : variant === "milestone" ? (
            <>
              <span className="ld-pool-label">Total Prize Pool</span>
              <span className="ld-pool-amt">{MILESTONE_POOL_LABEL}</span>
            </>
          ) : (
            <>
              <span className="ld-pool-label">Total Prize Pool</span>
              <span className="ld-pool-amt">{fmtINR(TOTAL_PRIZE_POOL)}</span>
            </>
          )}
        </div>
      </header>

      <div className="ld-body">
        <div className="ld-podium">
          {[1, 0, 2].map((idx) => {
            const r = top3[idx];
            const slot = idx + 1; // physical podium position (center/left/right) — layout only
            const rank = r ? r.rank : slot; // real, tie-aware standing shown to players
            const tiedWith = r ? tieGroupSize(rank) : 1;
            return (
              <div className={`ld-pcard ld-pcard--${slot}`} key={slot}>
                {r ? (
                  <>
                    <div className="ld-pmedal">
                      {rank <= 3 ? medal[rank - 1] : `#${rank}`}
                      {variant === "milestone" && r.crowned && <span className="ld-crown">👑</span>}
                    </div>
                    <div className="ld-pname">
                      {r._id}
                      {r.is_live && <span className="ld-live-badge"><span className="ld-live-dot" />LIVE</span>}
                    </div>
                    {variant === "milestone" ? (
                      <>
                        <div className="ld-psub ld-psub--bold">{fmtPts(r.total_points, 0)} pts</div>
                        <div className="ld-ppts-label">Hours</div>
                        <div className="ld-ppts"><AnimatedPoints id={r._id} value={r.total_hours} decimals={1} />h</div>
                      </>
                    ) : (
                      <>
                        <div className="ld-psub">{fmtHrs(r.total_minutes)} played</div>
                        <div className="ld-ppts-label">Points</div>
                        <div className="ld-ppts"><AnimatedPoints id={r._id} value={r.total_points} decimals={displayDecimals[r._id]} /></div>
                      </>
                    )}
                    {variant === "milestone" ? (
                      <>
                        {r.current_tier && <div className="ld-pprize">{fmtPrize(r.current_tier.prize)} secured</div>}
                        {r.next_tier && (
                          <div className="ld-progress">
                            {fmtPts(r.points_to_next, 0)} pts <b>or</b> {fmtHoursNeeded(r.hours_needed)} to <b>{fmtPrize(r.next_tier.prize)}</b>
                          </div>
                        )}
                      </>
                    ) : (
                      prizeForRank(rank, variant, tiedWith) && (
                        <div className="ld-pprize">
                          {fmtPrize(prizeForRank(rank, variant, tiedWith))}
                          {tiedWith > 1 && <span className="ld-ptied"> (split × {tiedWith})</span>}
                        </div>
                      )
                    )}
                  </>
                ) : (
                  <div className="ld-empty-podium">Rank {rank}<br />Awaiting player</div>
                )}
              </div>
            );
          })}
        </div>

        <div className="ld-list" ref={listRef}>
          {rankings.length === 0 && (
            <div className="ld-empty-list">No completed sessions yet. Be the first!</div>
          )}
          {pageRows.map((r, i) => {
            const positionalRank = page * pageSize + 4 + i;
            const rank = r ? r.rank : positionalRank;
            const tiedWith = r ? tieGroupSize(rank) : 1;
            const prize = prizeForRank(rank, variant, tiedWith);
            const rowRef = i === 0 ? firstRowRef : undefined;
            if (!r) {
              return (
                <div className="ld-row ld-row--empty" key={`empty-${positionalRank}`} ref={rowRef} style={{ animationDelay: `${i * 0.05}s` }}>
                  <div className="ld-row-rank">{rank}</div>
                  <div className="ld-row-name">
                    <b>Awaiting player</b>
                  </div>
                  {!isIphoneVariant(variant) && <div className="ld-row-prize">{prize ? fmtPrize(prize) : "—"}</div>}
                </div>
              );
            }
            return (
              <div className="ld-row" key={r._id} ref={rowRef} style={{ animationDelay: `${i * 0.05}s` }}>
                <div className="ld-row-rank">
                  {rank}
                  {variant === "milestone" && r.crowned && <span className="ld-crown">👑</span>}
                </div>
                <div className="ld-row-name">
                  <b>{r._id}</b>
                  {r.is_live && <span className="ld-live-badge"><span className="ld-live-dot" />LIVE</span>}
                  {variant === "milestone" ? (
                    <div className="ld-row-sub ld-row-sub--bold">{fmtPts(r.total_points, 0)} pts · {r.session_count} session{r.session_count > 1 ? "s" : ""}</div>
                  ) : (
                    <div className="ld-row-sub">{fmtHrs(r.total_minutes)} · {r.session_count} session{r.session_count > 1 ? "s" : ""}</div>
                  )}
                </div>
                {variant === "milestone" ? (
                  <div className="ld-row-pts"><AnimatedPoints id={r._id} value={r.total_hours} decimals={1} />h</div>
                ) : (
                  <div className="ld-row-pts"><AnimatedPoints id={r._id} value={r.total_points} decimals={displayDecimals[r._id]} /> pts</div>
                )}
                {variant === "milestone" ? (
                  <div className="ld-row-progress">
                    {r.current_tier ? fmtPrize(r.current_tier.prize) : "—"}
                    {r.next_tier && <div>{fmtPts(r.points_to_next, 0)} pts or <b>{fmtHoursNeeded(r.hours_needed)}</b> to next</div>}
                  </div>
                ) : !isIphoneVariant(variant) && (
                  <div className="ld-row-prize">
                    {prize ? fmtPrize(prize) : "—"}
                    {tiedWith > 1 && prize ? ` (split × ${tiedWith})` : ""}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {totalPages > 1 && (
          <div className="ld-page-dots">
            {Array.from({ length: totalPages }).map((_, i) => (
              <div key={i} className={`ld-page-dot${i === page ? " active" : ""}`} />
            ))}
          </div>
        )}
      </div>

      <footer className="ld-footer">
        {isIphoneVariant(variant) && (
          <div className="ld-toc">
            <span>Min. Buy-In: 50,000 {variant === "daily" ? "same-day" : "during the weekend"} to qualify</span>
            <span className="ld-toc-sep">•</span>
            <span>Refer at least 1 friend {variant === "daily" ? "same-day" : "during the weekend"}, and they must play a table</span>
          </div>
        )}
        <div className="ld-status-row">
          {contestEnded ? (
            <span>🏆 Final results — {variant === "milestone" ? "leaderboard" : "contest"} closed</span>
          ) : (
            <>
              <span>Live sessions update in real time</span>
              <span className="ld-live"><span className="ld-live-dot" />LIVE</span>
              {secondsAgo != null && <span>Updated {secondsAgo}s ago</span>}
            </>
          )}
        </div>
      </footer>

      {celebrating && <div className="ld-flash" />}
      {bangVisible && (
        <div className="ld-bang-wrap">
          <div className={`ld-bang-box${contestWinnerMode ? " ld-bang-box--day ld-bang-box--contest" : dayWinnerMode ? " ld-bang-box--day" : ""}`}>
            {contestWinnerMode ? (
              <>
                <div className="ld-bang-line1 ld-bang-line1--day">🏆 {variant === "milestone" ? "MILESTONE WINNERS" : "CONTEST WINNERS"}! 🏆</div>
                <div className="ld-bang-line2">{cfg.title} — Final Results</div>
                <div className="ld-winner-list">
                  {contestWinners.slice(0, 10).map((w, i) => (
                    <div className="ld-winner-row" key={`${w.rank}-${i}`}>
                      <span className="ld-winner-rank">{w.rank <= 3 ? medal[w.rank - 1] : `#${w.rank}`}</span>
                      <span className="ld-winner-name">{w.name}</span>
                      <span className="ld-winner-prize">{fmtPrize(w.prize)}</span>
                    </div>
                  ))}
                  {contestWinners.length > 10 && (
                    <div className="ld-winner-more">+{contestWinners.length - 10} more winners below</div>
                  )}
                  {contestWinners.length === 0 && (
                    <div className="ld-winner-more">No qualifying players this contest</div>
                  )}
                </div>
              </>
            ) : dayWinnerMode ? (
              <>
                <div className="ld-bang-line1 ld-bang-line1--day">🏆 {variant === "weekend" ? "THIS WEEKEND'S WINNER" : "TODAY'S WINNER"}! 🏆</div>
                <div className="ld-bang-line2">{bangName} wins the iPhone 17!</div>
              </>
            ) : (
              <>
                <div className="ld-bang-line1">🏆 NEW LEADER! 🏆</div>
                <div className="ld-bang-line2">{bangName} takes the top spot!</div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
